import React from 'react';

/**
 * Hover-driven anchored menu (Popper) controller. Opening cancels any pending close,
 * and closing is delayed so the pointer can travel from the trigger into the menu
 * without it collapsing. Shared by the notifications dropdown and its "more" submenus.
 */
export const useHoverMenu = (closeDelay = 120) => {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = React.useCallback((event?: React.MouseEvent<HTMLElement>) => {
    cancelScheduledClose();
    if (event) setAnchorEl(event.currentTarget);
    setOpen(true);
  }, [cancelScheduledClose]);

  const scheduleClose = React.useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), closeDelay);
  }, [cancelScheduledClose, closeDelay]);

  const close = React.useCallback(() => {
    cancelScheduledClose();
    setOpen(false);
  }, [cancelScheduledClose]);

  React.useEffect(() => () => cancelScheduledClose(), [cancelScheduledClose]);

  return { open, anchorEl, openMenu, scheduleClose, close };
};
